import { useEffect, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Modal } from "@impero-com/ui";
import * as Sentry from "@sentry/react";
import { Radio } from "antd";

import * as theme from "./ExportModal.css";
import { Footer } from "./Footer";

export interface ExportModalOption<T extends string> {
    value: T;
    label: string;
}

interface ExportModalProps<T extends string> {
    title: string;
    options: ExportModalOption<T>[];
    defaultValue: T;
    onSubmit: (type: T) => void;
    onClose: () => void;
    open: boolean;
    loading: boolean;
}

export function ExportModal<T extends string>({
    title,
    options,
    defaultValue,
    onSubmit,
    onClose,
    open,
    loading,
}: ExportModalProps<T>) {
    const { t } = useTranslation();
    const [submitType, setSubmitType] = useState<T>(defaultValue);

    // Start from the default again each time the modal opens.
    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSubmitType(defaultValue);
        }
    }, [open, defaultValue]);

    const handleOkClick = () => {
        if (!submitType) {
            const err = new Error(
                "Expected ok button to be disabled and non-interactive"
            );
            console.error(err);
            Sentry.captureException(err);
            return;
        }
        onSubmit(submitType);
    };

    return (
        <Modal
            variant="content"
            open={open}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={handleOkClick}
                    confirmLabel={t("Export")}
                    loading={loading}
                    okDisabled={!submitType}
                />
            }
            onClose={onClose}
            title={title}
            hideCloseButton={loading}
            disableDismiss={loading}
        >
            <Radio.Group
                className={theme.radioGroup}
                onChange={(e) => setSubmitType(e.target.value as T)}
                value={submitType}
                options={options}
            />
        </Modal>
    );
}
